import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Layout from '../../components/layout/Layout'
import { useAuthStore } from '../../store/authStore'
import back_app from '../../lib/back_app'

type BrandAsset = {
  id: string
  asset_type: 'logo' | 'product' | 'other'
  asset_url: string
  asset_name: string
}

export default function CampaignNew() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [assets, setAssets] = useState<BrandAsset[]>([])
  const [selectedAssets, setSelectedAssets] = useState<string[]>([])
  const [showAssetUpload, setShowAssetUpload] = useState(false)

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    budget: '',
    costPerView: '',
  })

  const [newAsset, setNewAsset] = useState({
    name: '',
    type: 'logo' as 'logo' | 'product' | 'other',
    file: null as File | null,
    preview: '',
  })

  useEffect(() => {
    loadAssets()
  }, [user])

  const loadAssets = async () => {
    if (!user) return
    let res = await back_app.basset_list()
    if (!res.err)
      setAssets(res.assets || [])
  }

  const handleAssetToggle = (assetId: string) => {
    setSelectedAssets((prev) =>
      prev.includes(assetId)
        ? prev.filter((id) => id !== assetId)
        : [...prev, assetId]
    )
  }

  const handleAssetFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    let file = e.target.files?.[0]
    if (file && file.type.startsWith('image/')) {
      setNewAsset({
        ...newAsset,
        file,
        preview: URL.createObjectURL(file),
      })
    }
  }

  const handleUploadNewAsset = async () => {
    if (!newAsset.file || !newAsset.name || !user) return

    let form_data = new FormData()
    form_data.append('file', newAsset.file)
    form_data.append('asset_type', newAsset.type)
    form_data.append('asset_name', newAsset.name)

    let res = await back_app.basset_insert(form_data)
    if (res.err) {
      alert('Upload failed: ' + res.err)
      return
    }

    
    let list_res = await back_app.basset_list()
    if (!list_res.err) {
      setAssets(list_res.assets || [])
      if (res.id)
        setSelectedAssets([...selectedAssets, res.id])
    }
    setNewAsset({ name: '', type: 'logo', file: null, preview: '' })
    setShowAssetUpload(false)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    if (selectedAssets.length === 0) {
      setError('Please select at least one asset for this campaign')
      setLoading(false)
      return
    }

    if (!user) return

    let budget = parseFloat(formData.budget)
    let cost_per_view = parseFloat(formData.costPerView)

    let res = await back_app.campaign_create({
      name: formData.name,
      selected_asset_ids: selectedAssets,
      budget_info: {
        budget,
        cost_per_view,
        remaining_budget: budget,
      },
      sched: {
        start_date: formData.startDate,
        end_date: formData.endDate,
      },
      status: 'active',
    })

    if (res.err) {
      setError(res.err)
      setLoading(false)
      return
    }

    navigate('/brand/campaigns')
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <button
            onClick={() => navigate('/brand/campaigns')}
            className="text-gray-600 hover:text-gray-900 flex items-center gap-2 mb-4"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Campaigns
          </button>
          <h1 className="text-3xl font-bold text-dark mb-2">Create New Campaign</h1>
          <p className="text-gray-600">Set up a new campaign to reach content creators</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              {error && (
                <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded mb-6">
                  <p className="text-sm">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-dark mb-2">Campaign Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-primary-purple transition"
                    placeholder="Summer Campaign 2025"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-dark mb-2">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-primary-purple transition"
                    placeholder="Brief description of your campaign goals..."
                    rows={4}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-dark mb-2">Start Date</label>
                    <input
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-primary-purple transition"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-dark mb-2">End Date</label>
                    <input
                      type="date"
                      value={formData.endDate}
                      onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-primary-purple transition"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-dark mb-2">Total Budget ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.budget}
                      onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-primary-purple transition"
                      placeholder="5000"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-dark mb-2">Cost Per View ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.costPerView}
                      onChange={(e) => setFormData({ ...formData, costPerView: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-primary-purple transition"
                      placeholder="0.05"
                      required
                    />
                  </div>
                </div>

                {formData.budget && formData.costPerView && (
                  <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-4">
                    <p className="text-sm text-purple-900">
                      <span className="font-semibold">Estimated reach:</span>{' '}
                      {Math.floor(parseFloat(formData.budget) / parseFloat(formData.costPerView)).toLocaleString()} views
                    </p>
                  </div>
                )}

                <div className="flex gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => navigate('/brand/campaigns')}
                    className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-bold hover:bg-gray-200 transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading || selectedAssets.length === 0}
                    className="flex-1 bg-gradient-to-r from-primary-purple to-primary-blue text-white py-3 rounded-xl font-bold hover:shadow-lg transition disabled:opacity-50"
                  >
                    {loading ? 'Creating...' : 'Create Campaign'}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-24">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-dark">Campaign Assets</h3>
                <button
                  type="button"
                  onClick={() => setShowAssetUpload(!showAssetUpload)}
                  className="text-primary-purple hover:text-primary-blue transition text-sm font-semibold"
                >
                  + Add New
                </button>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                Select assets to include ({selectedAssets.length} selected)
              </p>

              {showAssetUpload && (
                <div className="mb-4 p-4 bg-purple-50 rounded-xl border-2 border-purple-200">
                  <h4 className="font-semibold text-sm mb-3">Upload New Asset</h4>
                  <div className="space-y-3">
                    <input
                      type="text"
                      placeholder="Asset name"
                      value={newAsset.name}
                      onChange={(e) => setNewAsset({ ...newAsset, name: e.target.value })}
                      className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm"
                    />
                    <select
                      value={newAsset.type}
                      onChange={(e) => setNewAsset({ ...newAsset, type: e.target.value as any })}
                      className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm"
                    >
                      <option value="logo">Logo</option>
                      <option value="product">Product</option>
                      <option value="other">Other</option>
                    </select>
                    {!newAsset.preview ? (
                      <label className="block">
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-purple-400 transition">
                          <p className="text-xs text-gray-600">Upload Image</p>
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleAssetFileSelect}
                          className="hidden"
                        />
                      </label>
                    ) : (
                      <div className="relative">
                        <img src={newAsset.preview} className="w-full h-24 object-cover rounded-lg" />
                        <button
                          onClick={() => setNewAsset({ ...newAsset, file: null, preview: '' })}
                          className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded text-xs"
                        >
                          X
                        </button>
                      </div>
                    )}
                    <button
                      onClick={handleUploadNewAsset}
                      disabled={!newAsset.file || !newAsset.name}
                      className="w-full bg-primary-purple text-white py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
                    >
                      Upload
                    </button>
                  </div>
                </div>
              )}

              <div className="space-y-3 max-h-96 overflow-y-auto">
                {assets.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-sm text-gray-500">No assets yet</p>
                    <button
                      onClick={() => navigate('/brand/assets')}
                      className="text-primary-purple text-sm font-semibold mt-2"
                    >
                      Manage Assets
                    </button>
                  </div>
                ) : (
                  assets.map((asset) => (
                    <label
                      key={asset.id}
                      className={`flex items-center gap-3 p-3 border-2 rounded-xl cursor-pointer transition ${
                        selectedAssets.includes(asset.id)
                          ? 'border-primary-purple bg-purple-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedAssets.includes(asset.id)}
                        onChange={() => handleAssetToggle(asset.id)}
                        className="w-5 h-5"
                      />
                      <img src={asset.asset_url} className="w-12 h-12 object-cover rounded" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-dark">{asset.asset_name}</p>
                        <span className="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700">
                          {asset.asset_type}
                        </span>
                      </div>
                    </label>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  )
}